import { PageHeader } from "../components/PageHeader";
import { useSiteContent, resolveImageUrl } from "../context/ContentContext";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export default function About() {
  const { content } = useSiteContent();
  const { company, images, stats, about_stats, values, markets, about_page, about_mission_vision } = content;
  const aboutCopy = about_page || {};
  const missionVision = Array.isArray(about_mission_vision) ? about_mission_vision : [];
  const aboutStats = Array.isArray(about_stats) && about_stats.length > 0 ? about_stats : stats;
  const companyName = company?.name || aboutCopy.header_title_highlight_fallback || "Vissera";
  const headerIntro = (aboutCopy.header_intro || "").replace(/\{company\}/g, companyName);
  const storyP1 = (aboutCopy.story_p1_fallback || "").replace(/\{company\}/g, companyName);

  return (
    <>
      <PageHeader
        eyebrow={aboutCopy.header_eyebrow}
        title={
          <>
            {aboutCopy.header_title_prefix} <span className="theme-text-accent">{companyName}</span>
          </>
        }
        intro={headerIntro}
      />

      <section className="section-pad" data-testid="about-story">
        <div className="container-x grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <div className="eyebrow mb-4">{aboutCopy.story_eyebrow}</div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold theme-text-primary leading-tight tracking-tight">
              {aboutCopy.story_title}
            </h2>
            <div className="divider-gold mt-6 mb-7" />
            <p className="theme-text-secondary text-lg leading-relaxed">
              {storyP1}
            </p>
            <p className="theme-text-secondary text-lg leading-relaxed mt-4">
              {aboutCopy.story_p2}
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.1 }}
            className="aspect-[5/6] rounded-3xl overflow-hidden shadow-2xl">
            <img src={resolveImageUrl(images.istanbulBridge)} alt="Istanbul" className="w-full h-full object-cover" />
          </motion.div>
        </div>
      </section>

      <section className="theme-bg-secondary section-pad">
        <div className="container-x grid md:grid-cols-2 gap-6 sm:gap-8">
          {missionVision.map((b) => (
            <div key={b.label} className="card-soft p-6 sm:p-10" data-testid={`about-${b.label.toLowerCase()}`}>
              <div className="eyebrow mb-4">{b.label}</div>
              <h3 className="font-display text-2xl sm:text-3xl font-bold theme-text-primary leading-tight">{b.title}</h3>
              <div className="divider-gold my-5" />
              <p className="theme-text-secondary leading-relaxed">{b.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section-pad">
        <div className="container-x">
          <div className="eyebrow mb-4">{aboutCopy.values_eyebrow}</div>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold theme-text-primary tracking-tight mb-10 sm:mb-12 max-w-3xl">
            {aboutCopy.values_title}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:[grid-template-columns:repeat(auto-fit,minmax(210px,1fr))] gap-5">
            {values.map((v, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.05 }}
                className="card-soft p-6" data-testid={`value-${(v.title || "").toLowerCase()}`}>
                <div className="w-10 h-10 rounded-full bg-[var(--gold)] text-[var(--button-text)] flex items-center justify-center font-display font-bold text-sm mb-4">
                  {i + 1}
                </div>
                <h3 className="font-display text-xl font-bold theme-text-primary mb-2">{v.title}</h3>
                <p className="theme-text-secondary text-sm leading-relaxed">{v.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="theme-bg-secondary section-pad">
        <div className="container-x">
          <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
            <div className="lg:col-span-1">
              <div className="eyebrow mb-4">{aboutCopy.markets_eyebrow}</div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold theme-text-primary tracking-tight leading-tight">
                {aboutCopy.markets_title}
              </h2>
              <p className="theme-text-secondary mt-6 leading-relaxed">
                {aboutCopy.markets_body}
              </p>
            </div>
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-5">
              {markets.map((m, i) => (
                <div key={i} className="theme-surface rounded-2xl p-6" data-testid={`market-${(m.title || "").toLowerCase().replace(/\s+/g, "-")}`}>
                  <h3 className="font-display text-lg font-bold theme-text-primary">{m.title}</h3>
                  <p className="theme-text-secondary text-sm mt-2 leading-relaxed">{m.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="theme-bg-dark py-20 text-[var(--interactive-text)]">
        <div className="container-x grid grid-cols-1 sm:grid-cols-2 md:[grid-template-columns:repeat(auto-fit,minmax(180px,1fr))] gap-8 sm:gap-10">
          {aboutStats.map((s, i) => (
            <div key={i} className="text-center">
              <div className="font-display text-4xl sm:text-5xl md:text-6xl font-bold theme-text-accent mb-3">{s.value}</div>
              <div className="text-xs theme-panel-dark-subtle uppercase tracking-[0.18em]">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="section-pad">
        <div className="container-x">
          <div className="rounded-3xl theme-surface p-7 sm:p-10 md:p-16 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <div className="eyebrow mb-3">{aboutCopy.cta_eyebrow}</div>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold theme-text-primary">{aboutCopy.cta_title}</h2>
            </div>
            <Link to="/contact" className="btn-primary w-full sm:w-auto justify-center">
              {aboutCopy.cta_button} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
